import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Input, input, OnInit, signal } from '@angular/core';
import { Permission } from '../../services/data/permission';
import { Icons } from '../../util/Icons';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ImageDialogComponent } from '../image-dialog/image-dialog.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import { KeycloakService } from 'src/app/auth/keycloak.service';
import { AuthenticationUser, AuthService, PermissionObject } from '@eadsysteme/ewsauthclient-v1';
import { LayoutService } from '../../../@vex/services/layout.service';
import { fadeInUp400ms } from '../../../@vex/animations/fade-in-up.animation';
import { stagger40ms } from '../../../@vex/animations/stagger.animation';
import { SessionService } from '../../services/session.service';
import { SnackbarService } from '../../services/snackbar.service';
import { PermissionsDialogComponent } from './permissions-dialog/permissions-dialog.component';
import {
	MatCell,
	MatCellDef,
	MatColumnDef,
	MatHeaderCell,
	MatHeaderCellDef,
	MatHeaderRow,
	MatHeaderRowDef,
	MatRow,
	MatRowDef,
	MatTable,
	MatTableDataSource,
} from '@angular/material/table';
import { MatDialog, MatDialogActions, MatDialogContent, MatDialogRef, MatDialogTitle } from '@angular/material/dialog';
import { PermissionsService } from '../../services/permissions.service';
import dayjs from 'dayjs';
import { firstValueFrom } from 'rxjs';
import { ConfigService } from '../../services/config/config.service';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatButton, MatIconButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { AraPopoverComponent } from '../util/ara-popover/popover.component';
import { AraQrGeneratorComponent } from '../util/ara-qr-generator/ara-qr-generator.component';
import { DatePipe } from '@angular/common';
import PermissionTypeEnum = PermissionObject.PermissionTypeEnum;

@Component({
	selector: 'ara-permissions-table',
	templateUrl: './permissions-table.component.html',
	styleUrls: ['./permissions-table.component.scss'],
	animations: [fadeInUp400ms, stagger40ms],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [
		TranslocoDirective,
		MatDialogTitle,
		CdkDrag,
		CdkDragHandle,
		MatIconButton,
		MatIcon,
		MatDialogContent,
		MatProgressSpinner,
		MatTable,
		MatColumnDef,
		MatHeaderCellDef,
		MatHeaderCell,
		MatCellDef,
		MatCell,
		AraPopoverComponent,
		AraQrGeneratorComponent,
		MatHeaderRowDef,
		MatHeaderRow,
		MatRowDef,
		MatRow,
		MatDialogActions,
		MatButton,
		DatePipe,
	],
})
export class PermissionsTableComponent implements OnInit {
	private translocoService = inject(TranslocoService);
	icons = inject(Icons);
	dialog = inject(MatDialog);
	dialogRef = inject<MatDialogRef<PermissionsTableComponent>>(MatDialogRef);
	layoutService = inject(LayoutService);
	configService = inject(ConfigService);
	private permissionsService = inject(PermissionsService);
	private snackBar = inject(SnackbarService);
	private authService = inject(AuthService);
	private readonly keycloak = inject(KeycloakService);
	private sessionService = inject(SessionService);

	readonly showPermissionTypeSelect = input(signal(false));
	readonly disablePermissionTypeSelect = input(signal(false));
	@Input() canAddPermission = true;
	@Input() canModifyPermissions = true;
	@Input() roleTypeLabel?: string;
	@Input() permissionsDisabledReason?: string;
	private destroyRef = inject(DestroyRef);
	currentUserId?: string;
	tenantUnitId?: string;
	realEstateId?: string;
	personEmail?: string;
	permissionType?: PermissionObject.PermissionTypeEnum;

	dataSource: MatTableDataSource<Permission>;
	permissionCodeValidUntil: Date;

	dataChanged = false;
	loading = false;
	permissions: Array<Permission> = new Array<Permission>();
	displayedPermissionColumns = ['name', 'createDate', 'codeValidDate', 'codeActivatedOn', 'code', 'comment', 'edit'];
	private attention: string;
	private confirmMessage: string;
	private titleAdd: string;
	private titleEdit: string;

	constructor() {
		this.attention = this.translocoService.translate('common.attention');
		this.confirmMessage = this.translocoService.translate('common.confirmMessage');
		this.titleAdd = this.translocoService.translate('permissions.createPermission');
		this.titleEdit = this.translocoService.translate('user.titleEdit');
		this.dialogRef.disableClose = true; //disable default close operation

		this.permissionCodeValidUntil = dayjs().add(1, 'month').toDate();
	}

	public ngOnInit() {
		if (!this.canModifyPermissions) {
			this.displayedPermissionColumns = this.displayedPermissionColumns.filter((column) => column !== 'edit');
		}
		this.refreshPermissions();
	}

	close() {
		this.dialogRef.close(this.dataChanged);
	}

	public refreshPermissions() {
		this.loading = true;
		this.permissions = [];
		this.dataSource = new MatTableDataSource<Permission>([]);
		this.permissionsService
			.getAllPermissionsForPermissionUser(this.currentUserId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (permissionObjects) => {
					if (!permissionObjects) {
						return;
					}
					for (const permissionObj of permissionObjects) {
						const permission = this.permissionsService.permissionObjectToPermission(permissionObj);
						this.updateTable(permission);
					}
				},
				error: () => {
					this.loading = false;
				},
				complete: () => {
					this.loading = false;
				},
			});
	}

	private updateTable(permission: Permission) {
		this.permissions.push(permission);
		this.sortPermissions();
		this.dataSource = new MatTableDataSource<Permission>(this.permissions);
	}

	public showQrCode(permission: Permission) {
		const dialogRef = this.dialog.open(ImageDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		if (permission.authCode) {
			dialogRef.componentInstance.type = 'qr';
			dialogRef.componentInstance.permission = permission;
		} else {
			dialogRef.componentInstance.imageSrc = 'assets/img/qrcode.png';
		}
	}

	openDialogForm(action: string, permission?: Permission) {
		permission ??= {
			keyCloakUserId: undefined,
			firstName: undefined,
			lastName: undefined,
			authCode: undefined,
			createDate: new Date().toDateString(),
			permissionType: this.permissionType,
			permissionUserId: this.currentUserId,
			permissionCreatorId: this.keycloak.getKeycloakInstance().tokenParsed.sub,
			tenantUnitId: this.tenantUnitId,
			area: this.sessionService.effectiveArea,
			email: undefined,
			comment: undefined,
			codeActivatedOn: undefined,
			codeValidUntil: this.permissionCodeValidUntil.toDateString(),
		};
		const dialogRef = this.dialog.open(PermissionsDialogComponent, {
			minWidth: '300px',
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.title.set(action === 'edit' ? this.titleEdit : this.titleAdd);
		dialogRef.componentInstance.isEdit.set(action === 'edit');
		dialogRef.componentInstance.showPermissionTypeSelect = this.showPermissionTypeSelect();
		dialogRef.componentInstance.disablePermissionTypeSelect = this.disablePermissionTypeSelect();
		dialogRef.componentInstance.personEmail = this.personEmail;
		if (this.permissionType) {
			dialogRef.componentInstance.permissionTypeFC.setValue(this.permissionType);
		}
		if (permission) {
			dialogRef.componentInstance.permission = { ...permission };
		}
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe((result) => {
				if (result?.saved && result?.permission) {
					this.savePermission(result.permission, result.sendMail, result.type);
					this.dataChanged = true;
				}
			});
	}

	savePermission(permission: Permission, sendMail: boolean = false, type: PermissionTypeEnum = undefined) {
		if (type) {
			permission.permissionType = type;
		}
		const authUserTO: AuthenticationUser = this.permissionsService.createAuthUser(permission);
		this.authService
			.generateAuthCodeForRealEstate(this.sessionService.effectiveArea, sendMail, authUserTO)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				error: () => this.snackBar.error(this.translocoService.translate('errors.permissionCreate')),
				complete: () => this.refreshPermissions(),
			});
		this.dataChanged = true;
	}

	editPermission(permission: Permission) {
		if (!this.canModifyPermissions) {
			return;
		}
		this.openDialogForm('edit', permission);
	}

	deletePermission(permission: Permission) {
		if (!this.canModifyPermissions) {
			return;
		}
		const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
			disableClose: false,
			autoFocus: false,
			restoreFocus: false,
		});
		dialogRef.componentInstance.titleMessage = this.attention;
		dialogRef.componentInstance.confirmMessage = this.confirmMessage;
		dialogRef
			.afterClosed()
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe(async (result) => {
				if (result) {
					this.dataChanged = true;
					await firstValueFrom(this.permissionsService.deleteGroupPermissionByAuthCode(permission.area, permission.authCode)).then(() => {
						this.snackBar.success(this.translocoService.translate('common.deleted'));
						this.refreshPermissions();
					});
				}
			});
	}

	addNewPermission() {
		this.openDialogForm('add');
	}

	canShowAddPermissionButton(): boolean {
		return this.canAddPermission && !!this.currentUserId;
	}

	hasVisibleCodes(): boolean {
		return (this.permissions ?? []).some((permission) => !!permission?.authCode && !permission?.codeActivatedOn);
	}

	sortPermissions() {
		this.permissions.sort((a, b) => (dayjs(b.codeValidUntil).isAfter(dayjs(a.codeValidUntil)) ? 1 : -1));
	}

	setPersonEmail(email?: string): void {
		this.personEmail = email;
	}
}
